import { useEffect, useState } from 'react';
import { GLOW_STD, TONE_VARS, NEON_VARS } from './chartTokens';

/* ──────────────────────────────────────────────────────────────────────────
   useChartTheme — resolves the v4 CSS-variable tokens to concrete colours so the
   chart SVGs can use them reliably as attributes (var() in SVG presentation
   attributes is flaky across browsers). Re-resolves whenever the brand theme
   flips, so charts re-colour and the glow dials back in light mode (§5).
   ────────────────────────────────────────────────────────────────────────── */

const readTheme = () => {
  if (typeof document === 'undefined') {
    return { glowStd: GLOW_STD.dark, isLight: false, tone: {}, neon: [] };
  }
  const cs = getComputedStyle(document.documentElement);
  const v = (name) => cs.getPropertyValue(name).trim();
  const isLight = document.documentElement.getAttribute('data-theme') === 'light';

  return {
    isLight,
    glowStd: isLight ? GLOW_STD.light : GLOW_STD.dark,
    tone: Object.fromEntries(Object.entries(TONE_VARS).map(([t, name]) => [t, v(name)])),
    neon: NEON_VARS.map((n) => (n.startsWith('--') ? v(n) : n)),
    ink2: v('--arc-ink-2'),
    ink3: v('--arc-ink-3'),
    line: v('--arc-line'),
    paper: v('--arc-paper'),
    accent: v('--arc-accent'),
  };
};

export default function useChartTheme() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const update = () => setTheme(readTheme());
    update(); // resolve once mounted, in case the initial read raced the stylesheet
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  return theme;
}
